import type { FormField, UseCaseConfig } from "./types";

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbyRjbaqF_JF-EbFsT0F6BX-Y02-SrK8nttFyOM4l0Mp8zf6yucDI5UldjpD_PBdsbYMgQ/exec";

/**
 * The shape posted to the Apps Script endpoint. The script writes the core
 * keys (`first_name`, `last_name`, `work_email`, `audience_size`,
 * `current_yir_program`) to dedicated sheet columns, stringifies
 * `vertical_answers` and `source_utm` into their own columns, and uses
 * `sample_url` to build the P.S. line of the auto-reply email.
 *
 * The core keys are optional here because the payload builder omits any field
 * left blank (see `submitUseCaseForm`); the wizard still requires them.
 *
 * Leads are captured in two stages so drop-offs are still followable: the
 * wizard posts a `partial` row as soon as the email step is done, then the
 * full `complete` submission. Both carry the same `submission_id`; the script
 * upserts on it and only sends the auto-reply for `complete`. See
 * doc/reference/lead-capture.md.
 */
export interface SubmissionPayload {
  use_case: string;
  first_name?: string;
  last_name?: string;
  work_email?: string;
  audience_size?: string;
  current_yir_program?: string;
  vertical_answers: Record<string, unknown>;
  source_utm: Record<string, string>;
  /**
   * Full URL to the vertical's sample narrative, with UTM/source params.
   * Empty when the vertical's reportId is a placeholder sentinel.
   */
  sample_url: string;
  client_timestamp: string;
  /** Per-visit id shared by the partial and complete posts (upsert key). */
  submission_id: string;
  stage: SubmissionStage;
}

export type SubmissionStage = "partial" | "complete";

/** Fresh id for one pass through a form. */
export function newSubmissionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

function readUtmParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) out[key.replace(/^utm_/, "")] = v;
  }
  return out;
}

/** Look up the human-readable label for a select option; falls back to the raw value. */
function toDisplayValue(field: FormField | undefined, raw: string): string {
  if (!field || field.type !== "select") return raw;
  const match = field.options.find((o) => o.value === raw);
  return match ? match.label : raw;
}

/**
 * Build the auto-reply P.S. URL for this vertical's sample narrative. Returns
 * an empty string when the vertical's reportId is a sentinel (e.g.
 * `placeholder_customer_marketing`), empty (e.g. the home page's general
 * config), or absent (a gallery-only vertical, whose samples live off-site)
 * so the email can drop its P.S. block instead of linking to a 404.
 */
function buildSampleUrl(config: UseCaseConfig): string {
  const reportId = config.narrativeSample?.reportId;
  if (!reportId || reportId.startsWith("placeholder")) return "";
  const params = new URLSearchParams({
    report: reportId,
    src: "auto_reply",
    utm_medium: "email",
  });
  return `${window.location.origin}/?${params.toString()}`;
}

/**
 * Split form `values` by `field.core` into the top-level core columns and
 * the `vertical_answers` blob, convert select values to their labels (so the
 * sheet stores "First 90 days" instead of "first-90-days"), attach UTM +
 * sample URL, and POST to the Apps Script endpoint.
 *
 * The body is JSON but labeled `text/plain;charset=UTF-8` so the request
 * stays "CORS-simple" (no preflight) — Apps Script's `doPost` reads the raw
 * body regardless of content type and `JSON.parse`s it on the server side.
 * That lets us drop `mode: 'no-cors'` and actually inspect the response, so
 * a non-2xx status (or network failure) surfaces as a thrown error instead
 * of a silent success.
 */
export async function submitUseCaseForm(args: {
  config: UseCaseConfig;
  values: Record<string, unknown>;
  /** Caller-provided UTM params captured at mount; falls back to current URL. */
  utm?: Record<string, string>;
  submissionId: string;
  /** Defaults to `complete` (the final submit). */
  stage?: SubmissionStage;
}): Promise<void> {
  const { config, values } = args;
  const fieldByKey = new Map(config.cta.fields.map((f) => [f.key, f]));
  const coreKeys = new Set(
    config.cta.fields.filter((f) => f.core).map((f) => f.key),
  );

  const corePayload: Partial<SubmissionPayload> = {};
  const verticalAnswers: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === "") continue;
    const field = fieldByKey.get(key);
    const display =
      typeof value === "string" ? toDisplayValue(field, value) : value;
    if (coreKeys.has(key)) {
      (corePayload as Record<string, unknown>)[key] = display;
    } else {
      verticalAnswers[key] = display;
    }
  }

  const payload: SubmissionPayload = {
    use_case: config.slug,
    vertical_answers: verticalAnswers,
    source_utm: args.utm ?? readUtmParams(window.location.search),
    sample_url: buildSampleUrl(config),
    client_timestamp: new Date().toISOString(),
    submission_id: args.submissionId,
    stage: args.stage ?? "complete",
    ...corePayload,
  };

  const response = await fetch(GOOGLE_SCRIPT_URL, {
    method: "POST",
    // Intentionally `text/plain` (not `application/json`) so the request is
    // CORS-simple and skips the preflight Apps Script can't handle. The body
    // is still JSON; Apps Script parses `e.postData.contents` server-side.
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      `Form submission failed: ${response.status} ${response.statusText}`,
    );
  }

  // Apps Script answers 200 even when doPost throws, reporting the failure as
  // `{ success: false }` in the body, so a failed sheet write must be read
  // from there. A body that isn't JSON is treated as success (unchanged
  // behavior for any deployment that doesn't return the envelope).
  let result: unknown = null;
  try {
    result = await response.json();
  } catch {
    return;
  }
  if (
    result &&
    typeof result === "object" &&
    (result as { success?: unknown }).success === false
  ) {
    throw new Error(
      `Form submission failed: ${String((result as { error?: unknown }).error ?? "unknown error")}`,
    );
  }
}

/**
 * Save the lead as soon as the email step is done, so a visitor who stops
 * partway through is still followable. Fire-and-forget: a failure here must
 * never block the visitor from continuing — the final submit re-sends
 * everything under the same `submissionId`.
 */
export function savePartialLead(args: {
  config: UseCaseConfig;
  workEmail: string;
  utm?: Record<string, string>;
  submissionId: string;
}): void {
  submitUseCaseForm({
    config: args.config,
    values: { work_email: args.workEmail },
    utm: args.utm,
    submissionId: args.submissionId,
    stage: "partial",
  }).catch((err) => console.warn("Partial lead save failed:", err));
}

export { GOOGLE_SCRIPT_URL, readUtmParams };
